import { Component, h } from 'preact'
import Phrase from '../../libs/translationsUtils/phrase'
import axios from 'axios'
import { Leaderboard } from './interfaces'
import {
  LeaderboardEmptyMessage,
  LeaderboardSkeleton,
  LeaderboardUserListTable
} from './components'

const DEFAULT_PAGE = 1
const DEFAULT_PAGE_SIZE = 50
const PERIOD_THIS_WEEK = 'thisWeek'
const PERIOD_ALL_TIME = 'allTime'

interface Props {
    period: string,
    isGuest: boolean,
    loggedInUserId: string
}

interface State {
    weekly: Leaderboard,
    allTime: Leaderboard,
    period: string,
    isLoading: boolean,
    loggedInUserId: string|null
}

class LeaderboardOverview extends Component<Props, State> {
  constructor (props: Props) {
    super(props)
    this.state = {
      weekly: {
        usersList: [],
        totalPages: 0,
        currentPage: DEFAULT_PAGE,
        currentUser: undefined
      },
      allTime: {
        usersList: [],
        totalPages: 0,
        currentPage: DEFAULT_PAGE,
        currentUser: undefined
      },
      period: props.period,
      isLoading: false,
      loggedInUserId: props.loggedInUserId
    }
    this.fetchLeaderboard = this.fetchLeaderboard.bind(this)
    this.fetchLoggedInLeaderboardUser = this.fetchLoggedInLeaderboardUser.bind(this)
    this.fetchData = this.fetchData.bind(this)
    this.generateNextUrl = this.generateNextUrl.bind(this)
  }

  async componentDidMount () {
    const params = new URLSearchParams(window.location.search)
    const page = params.get('page') || '1'
    await this.fetchData(this.props.period, parseInt(page))
  }

  async fetchData (period: string, page: number = DEFAULT_PAGE) {
    if (period !== this.state.period) {
      window.history.pushState(null, '', 'leaderboard')
    }
    this.setState({ period: period, isLoading: true })
    const fetchLoggedInUserSection = !this.props.isGuest && page === 1
    const [leaderboard, leaderboardUser] = await Promise.all([this.fetchLeaderboard(period, page), fetchLoggedInUserSection ? this.fetchLoggedInLeaderboardUser(period) : undefined])
    const { result, totalPages, currentPage } = leaderboard
    const currentUser = leaderboardUser

    if (period === PERIOD_THIS_WEEK) {
      const weeklyData = {
        usersList: result,
        totalPages: totalPages,
        currentPage: currentPage,
        currentUser: currentUser
      }
      this.setState({ weekly: weeklyData, isLoading: false })
    } else {
      const allTimeData = {
        usersList: result,
        totalPages: totalPages,
        currentPage: currentPage,
        currentUser: currentUser
      }
      this.setState({ allTime: allTimeData, isLoading: false })
    }
  }

  async fetchLeaderboard (period: string, page: number = DEFAULT_PAGE) {
    try {
      const res = await axios.get('/leaderboard/fetch', {
        params: {
          period: period,
          page: page,
          pageSize: DEFAULT_PAGE_SIZE
        }
      })

      if (res.data) {
        return res.data
      }
    } catch {
      return []
    }
  }

  async fetchLoggedInLeaderboardUser (period: string) {
    try {
      const res = await axios.get('/leaderboard/fetchUser', {
        params: {
          period: period
        }
      })
      if (Object.keys(res.data).length !== 0) {
        return res.data
      }
    } catch {
      return undefined
    }
  }

  generateNextUrl (newPage: number) {
    const { period } = this.state
    const baseUrl = '/leaderboard'
    const newPagination = newPage === 1 ? `?period=${period}` : `?period=${period}&page=${newPage}`
    return `${window.location.origin}${baseUrl}${newPagination}`
  }

  render ({ isGuest }: Props, { period, weekly, allTime, isLoading, loggedInUserId }: State) {
    const leaderboardData = period === PERIOD_THIS_WEEK ? weekly : allTime
    const { usersList, totalPages, currentPage, currentUser } = leaderboardData
    const isLeaderboardEmpty = usersList.length === 0
    const showCurrentlyLoggedInUserSection = !isGuest && currentPage === 1

    return (
      <div className='leaderboard-overview-container'>
        <div className='leaderboard-header-container'>
          <div><h1 className='leaderboard-overview-title'> <Phrase p='leaderboard.page.title' /> </h1>  <div className='leaderboard-overview-subtitle'> <Phrase p='leaderboard.page.subtitle' /></div></div>

          <div className='leaderboard-time-period-container'>
            <div className={period === 'thisWeek' ? 'link--active' : 'link--text'} onClick={() => this.fetchData(PERIOD_THIS_WEEK)}>
              <Phrase p='leaderboard.points.thisWeek' />
            </div>
            <div className='leaderboard-time-period-separator'> |</div>
            <div className={period === 'allTime' ? 'link--active' : 'link--text'} onClick={() => this.fetchData(PERIOD_ALL_TIME)}>
              <Phrase p='leaderboard.points.allTime' />
            </div>
          </div>
        </div>
        <div>
          {!isLoading && isLeaderboardEmpty && <LeaderboardEmptyMessage /> }
          {isLoading || isLeaderboardEmpty ? <LeaderboardSkeleton isLoading={isLoading} /> : null}
          {!isLoading && !isLeaderboardEmpty && <LeaderboardUserListTable usersList={usersList}
            showCurrentlyLoggedInUserSection={showCurrentlyLoggedInUserSection}
            currentUser={currentUser}
            loggedInUserId={Number(loggedInUserId)}
            totalPages={totalPages}
            currentPage={currentPage}
            generateNewUrl={this.generateNextUrl} />
          }
        </div>
      </div>
    )
  }
}
export default LeaderboardOverview
